// Regras de agenda e exibição sempre em America/Sao_Paulo; o banco guarda UTC.
export const TZ = "America/Sao_Paulo";

export const WEEKDAYS_PT = [
  "domingo",
  "segunda",
  "terça",
  "quarta",
  "quinta",
  "sexta",
  "sábado",
] as const;

export const WEEKDAYS_SHORT_PT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"] as const;

export interface SpParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 = domingo */
  weekday: number;
}

const partsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  weekday: "short",
  hourCycle: "h23",
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function spParts(date: Date): SpParts {
  const map: Record<string, string> = {};
  for (const p of partsFormatter.formatToParts(date)) map[p.type] = p.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    weekday: WEEKDAY_INDEX[map.weekday ?? "Sun"] ?? 0,
  };
}

/** Data de hoje em São Paulo no formato YYYY-MM-DD. */
export function spDateString(date: Date): string {
  const p = spParts(date);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** Minutos desde 00h00 em São Paulo. */
export function spMinuteOfDay(date: Date): number {
  const p = spParts(date);
  return p.hour * 60 + p.minute;
}

export function monthKey(date: Date): string {
  const p = spParts(date);
  return `${p.year}-${pad2(p.month)}`;
}

/** Instante UTC de um horário local de São Paulo. */
export function spToUtc(dateString: string, minuteOfDay = 0): Date {
  const [y, m, d] = dateString.split("-").map(Number) as [number, number, number];
  const h = Math.floor(minuteOfDay / 60);
  const min = minuteOfDay % 60;
  // Primeira estimativa com UTC-3 e correção pelo deslocamento real do fuso.
  let guess = Date.UTC(y, m - 1, d, h + 3, min);
  for (let i = 0; i < 2; i++) {
    const p = spParts(new Date(guess));
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    const wanted = Date.UTC(y, m - 1, d, h, min);
    const diff = wanted - asUtc;
    if (diff === 0) break;
    guess += diff;
  }
  return new Date(guess);
}

export function startOfSpMonth(date: Date): Date {
  const p = spParts(date);
  return spToUtc(`${p.year}-${pad2(p.month)}-01`);
}

export function addMonthsToMonthStart(monthStart: Date, months: number): Date {
  const p = spParts(monthStart);
  const total = p.year * 12 + (p.month - 1) + months;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return spToUtc(`${y}-${pad2(m)}-01`);
}

export function addDays(dateString: string, days: number): string {
  const [y, m, d] = dateString.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** Diferença em dias entre duas datas YYYY-MM-DD (b menos a). */
export function diffDays(a: string, b: string): number {
  const [ya, ma, da] = a.split("-").map(Number) as [number, number, number];
  const [yb, mb, db] = b.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86_400_000);
}

export function weekdayOf(dateString: string): number {
  const [y, m, d] = dateString.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** 870 vira "14h30"; 480 vira "08h00". */
export function formatMinuteOfDay(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${pad2(h)}h${pad2(m)}`;
}

/** 60 vira "1h"; 90 vira "1h30"; 30 vira "30 min". */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h${pad2(m)}`;
}

/** "12/10/2026" a partir de Date (em São Paulo) ou de YYYY-MM-DD. */
export function formatDateBR(value: Date | string): string {
  if (typeof value === "string") {
    const [y, m, d] = value.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  const p = spParts(value);
  return `${pad2(p.day)}/${pad2(p.month)}/${p.year}`;
}

/** "12/10/2026 14h30" em São Paulo. */
export function formatDateTimeBR(value: Date): string {
  const p = spParts(value);
  return `${pad2(p.day)}/${pad2(p.month)}/${p.year} ${pad2(p.hour)}h${pad2(p.minute)}`;
}

export function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

const MONTHS_PT = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
] as const;

/** "2026-10" vira "outubro de 2026". */
export function formatMonthBR(key: string): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  return `${MONTHS_PT[m - 1]} de ${y}`;
}
